import { useEffect, useState } from 'react'
import { publicApi } from '../../lib/api'
import { setRefreshToken, setToken } from '../../lib/auth'

export function sanitizeHandoffNext(nextRaw: string | null | undefined): string {
  const next = String(nextRaw || '').trim()
  if (!next.startsWith('/')) return '/settings/wallet/buy'
  if (next.startsWith('//')) return '/settings/wallet/buy'
  return next
}

export function loginFallbackForNext(nextPath: string): string {
  return `/login?next=${encodeURIComponent(nextPath || '/settings/wallet/buy')}`
}

type HandoffIdentity = {
  id?: number | string
  username?: string
  profile?: { handle?: string }
}

export async function resolveHandoffRedirectTarget(
  search: string,
  exchange: (token: string) => Promise<void>,
  fetchMe?: () => Promise<HandoffIdentity>,
): Promise<{ target: string; authedAs: { id: number | string; handle: string } | null }> {
  const params = new URLSearchParams(search || '')
  const token = String(params.get('t') || '').trim()
  const nextPath = sanitizeHandoffNext(params.get('next'))

  if (!token) {
    return { target: loginFallbackForNext(nextPath), authedAs: null }
  }

  try {
    await exchange(token)
    let authedAs: { id: number | string; handle: string } | null = null
    if (fetchMe) {
      try {
        const me = await fetchMe()
        const handle = String(me?.profile?.handle || me?.username || '').trim()
        if (me?.id != null) {
          authedAs = { id: me.id, handle }
        }
      } catch {
        // identity logging is best effort only
      }
    }
    return { target: nextPath || '/settings/wallet/buy', authedAs }
  } catch {
    return { target: loginFallbackForNext(nextPath), authedAs: null }
  }
}

export default function AuthHandoff() {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const url = new URL(window.location.href)
      const result = await resolveHandoffRedirectTarget(url.search, async (token) => {
        const response = await publicApi.post(
          '/api/auth/web-handoff/exchange/',
          { token },
          { withCredentials: true },
        )
        const access = String(response?.data?.access || '').trim()
        const refresh = String(response?.data?.refresh || '').trim()
        if (access) setToken(access)
        if (refresh) setRefreshToken(refresh)
      }, async () => {
        const response = await publicApi.get('/api/me/', { withCredentials: true })
        return response.data
      })
      if (result.authedAs) {
        console.log('[handoff] authed_as', result.authedAs.id, result.authedAs.handle)
      }
      if (!cancelled) {
        window.location.replace(result.target)
      }
      if (result.target.startsWith('/login')) {
        if (cancelled) return
        setError('Unable to sign you in automatically.')
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="mx-auto max-w-lg p-6 text-white">
      <h1 className="text-xl font-semibold">Signing you in...</h1>
      <p className="mt-2 text-white/70">Please wait while we complete your secure handoff.</p>
      {error ? <p className="mt-3 text-rose-300">{error}</p> : null}
    </div>
  )
}
